import EnterpriseIcon, { LogoutIcon } from '@enterprise-ui/icons'
import { useRef, useState } from 'react'
import {
  ProfileActionButton,
  ProfileActionContainer,
  ProfileActionItem,
  ProfileContent,
  UserAvatarContainer,
  UserInitials,
  UserPreferredName,
  MigrationNotice,
  MigrationNoticeWarning,
  MigrationNoticeIcon,
  MigrationNoticeBody,
  MigrationNoticeTitle,
  MigrationNoticeText,
  MigrationNoticeLearnMore,
  MigrationNoticeLearnMoreWarning,
  BottomSheetOverlay,
  BottomSheet,
  BottomSheetHandle,
  BottomSheetHeader,
  BottomSheetTitle,
  BottomSheetCloseButton,
  BottomSheetBody,
} from './styles'
import MigrationPage from '../MigrationPage'

interface ProfileProps {
  onLogoutClick: Function
  userLegalName: string
  showMigrationNotice?: boolean
  isInWebBrowser?: boolean
  isInOldAppStoreApp?: boolean
  deploymentMode?: 'mdm' | 'manual'
  sunsetDate?: string | null
}

const Profile = ({
  userLegalName,
  onLogoutClick,
  showMigrationNotice = false,
  isInWebBrowser,
  isInOldAppStoreApp,
  deploymentMode = 'mdm',
  sunsetDate = null,
}: ProfileProps) => {
  const [migrationDetailsOpen, setMigrationDetailsOpen] = useState(false)
  const [dragY, setDragY] = useState(0)
  const dragStartY = useRef<number | null>(null)
  const DISMISS_THRESHOLD = 80

  function onDragStart(clientY: number) {
    if (window.innerWidth >= 768) return
    dragStartY.current = clientY
  }

  function onDragMove(clientY: number) {
    if (dragStartY.current === null) return
    const delta = clientY - dragStartY.current
    setDragY(Math.max(0, delta))
  }

  function onDragEnd() {
    if (dragY >= DISMISS_THRESHOLD) {
      setMigrationDetailsOpen(false)
    }
    dragStartY.current = null
    setDragY(0)
  }
  const splitUserFirstLastName = userLegalName.split(' ')
  const userFirstName = splitUserFirstLastName[0]
  const userLastName = splitUserFirstLastName[1]
  return (
    <ProfileContent>
      <UserAvatarContainer>
        <UserInitials>{`${userFirstName[0]}${userLastName[0]}`}</UserInitials>
      </UserAvatarContainer>
      <UserPreferredName>{userLegalName}</UserPreferredName>

      {showMigrationNotice && (
        <>
          {isInWebBrowser ? (
            <MigrationNoticeWarning>
              <MigrationNoticeIcon
                src="/mytvs.png"
                alt="New TruEye Mobile icon"
              />
              <MigrationNoticeBody>
                <MigrationNoticeTitle>
                  Update your bookmark
                </MigrationNoticeTitle>
                <MigrationNoticeText>
                  TruEye Mobile is moving to a new URL. Update your bookmark
                  before this one is decommissioned.
                </MigrationNoticeText>
                <MigrationNoticeLearnMoreWarning
                  onClick={() => setMigrationDetailsOpen(true)}
                  aria-expanded={migrationDetailsOpen}
                >
                  Show me the new URL
                </MigrationNoticeLearnMoreWarning>
              </MigrationNoticeBody>
            </MigrationNoticeWarning>
          ) : (
            <MigrationNotice>
              <MigrationNoticeIcon src="/mytvs.png" alt="New TruEye app icon" />
              <MigrationNoticeBody>
                <MigrationNoticeTitle>
                  A new TruEye Mobile app is on the way
                </MigrationNoticeTitle>
                <MigrationNoticeText>
                  A refreshed TruEye Mobile is coming to your device. Keep an
                  eye out for the new icon on your home screen.
                </MigrationNoticeText>
                <MigrationNoticeLearnMore
                  onClick={() => setMigrationDetailsOpen(true)}
                  aria-expanded={migrationDetailsOpen}
                >
                  Learn more
                </MigrationNoticeLearnMore>
              </MigrationNoticeBody>
            </MigrationNotice>
          )}

          {migrationDetailsOpen && (
            <BottomSheetOverlay
              onClick={() => setMigrationDetailsOpen(false)}
              aria-modal="true"
              role="dialog"
              aria-label="Migration details"
            >
              <BottomSheet
                onClick={(e) => e.stopPropagation()}
                style={{
                  transform: `translateY(${dragY}px)`,
                  transition: dragStartY.current
                    ? 'none'
                    : 'transform 0.3s ease-out',
                }}
                onMouseLeave={onDragEnd}
                onMouseUp={onDragEnd}
              >
                <BottomSheetHandle
                  onMouseDown={(e) => onDragStart(e.clientY)}
                  onMouseMove={(e) => e.buttons === 1 && onDragMove(e.clientY)}
                  onTouchStart={(e) => onDragStart(e.touches[0].clientY)}
                  onTouchMove={(e) => onDragMove(e.touches[0].clientY)}
                  onTouchEnd={onDragEnd}
                />
                <BottomSheetHeader
                  onMouseDown={(e) => onDragStart(e.clientY)}
                  onMouseMove={(e) => e.buttons === 1 && onDragMove(e.clientY)}
                  onTouchStart={(e) => onDragStart(e.touches[0].clientY)}
                  onTouchMove={(e) => onDragMove(e.touches[0].clientY)}
                  onTouchEnd={onDragEnd}
                >
                  <BottomSheetTitle>What's coming</BottomSheetTitle>
                  <BottomSheetCloseButton
                    onClick={() => setMigrationDetailsOpen(false)}
                    onMouseDown={(e) => e.stopPropagation()}
                    aria-label="Close details"
                  >
                    ✕
                  </BottomSheetCloseButton>
                </BottomSheetHeader>
                <BottomSheetBody>
                  <MigrationPage
                    isInWebBrowser={isInWebBrowser}
                    isInOldAppStoreApp={isInOldAppStoreApp}
                    deploymentMode={deploymentMode}
                    sunsetDate={sunsetDate}
                  />
                </BottomSheetBody>
              </BottomSheet>
            </BottomSheetOverlay>
          )}
        </>
      )}

      <ProfileActionContainer>
        {/* <ProfileActionItem>
          <ProfileActionButton iconOnly aria-label={'app information'}>
            <EnterpriseIcon icon={InfoIcon} />
          </ProfileActionButton>
          About
        </ProfileActionItem> */}
        <ProfileActionItem onClick={() => onLogoutClick()}>
          <ProfileActionButton iconOnly aria-label={'logout of app'}>
            <EnterpriseIcon icon={LogoutIcon} />
          </ProfileActionButton>
          Logout
        </ProfileActionItem>
      </ProfileActionContainer>
    </ProfileContent>
  )
}

export default Profile
